import * as THREE from 'three';

export interface MapResult {
  colliders: THREE.Box3[];
  objects: THREE.Object3D[];
  playerSpawn: THREE.Vector3;
  zombieSpawnArea: { min: THREE.Vector3; max: THREE.Vector3 };
}

export class MapBuilder {
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  buildStage(stageId: number): MapResult {
    // Clear existing map objects (keep lights and camera)
    const toRemove: THREE.Object3D[] = [];
    this.scene.traverse(child => {
      if (child.userData.isMapObject) toRemove.push(child);
    });
    toRemove.forEach(obj => this.scene.remove(obj));

    switch (stageId) {
      case 1: return this.buildLaboratory();
      case 2: return this.buildCity();
      case 3: return this.buildHospital();
      default: return this.buildGenericIndoor(stageId);
    }
  }

  private addMesh(
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    position: [number, number, number],
    colliders: THREE.Box3[],
    objects: THREE.Object3D[],
    rotation?: [number, number, number],
    isCollider = true
  ): THREE.Mesh {
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(...position);
    if (rotation) mesh.rotation.set(...rotation);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData.isMapObject = true;
    this.scene.add(mesh);
    objects.push(mesh);

    if (isCollider) {
      const box = new THREE.Box3().setFromObject(mesh);
      colliders.push(box);
    }
    return mesh;
  }

  private buildLaboratory(): MapResult {
    const colliders: THREE.Box3[] = [];
    const objects: THREE.Object3D[] = [];

    // --- Materials ---
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.9 });
    const floorTileLight = new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.85 });
    const floorTileDark = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.95 });
    const floorTileCracked = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 1.0 });
    const wallMat = new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.8 });
    const wallPanelMat = new THREE.MeshStandardMaterial({ color: 0x444444, roughness: 0.7, metalness: 0.1 });
    const metalMat = new THREE.MeshStandardMaterial({ color: 0x555555, roughness: 0.3, metalness: 0.8 });
    const darkMetalMat = new THREE.MeshStandardMaterial({ color: 0x333333, roughness: 0.4, metalness: 0.9 });
    const labMat = new THREE.MeshStandardMaterial({ color: 0x445566, roughness: 0.5 });
    const screenMat = new THREE.MeshStandardMaterial({ color: 0x113322, emissive: 0x113322, emissiveIntensity: 0.4, roughness: 0.3 });
    const screenOffMat = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.2, metalness: 0.5 });
    const glassMat = new THREE.MeshStandardMaterial({ color: 0x88ccff, roughness: 0.1, metalness: 0.2, transparent: true, opacity: 0.35 });
    const brokenGlassMat = new THREE.MeshStandardMaterial({ color: 0x99ddff, roughness: 0.05, metalness: 0.3, transparent: true, opacity: 0.5 });
    const pipeMat = new THREE.MeshStandardMaterial({ color: 0x666666, roughness: 0.4, metalness: 0.7 });
    const rustPipeMat = new THREE.MeshStandardMaterial({ color: 0x884422, roughness: 0.8, metalness: 0.5 });
    const bloodMat = new THREE.MeshStandardMaterial({ color: 0x550000, roughness: 0.95, transparent: true, opacity: 0.7 });
    const redMat = new THREE.MeshStandardMaterial({ color: 0xcc0000, roughness: 0.6 });
    const whiteMat = new THREE.MeshStandardMaterial({ color: 0xcccccc, roughness: 0.7 });
    const greenMat = new THREE.MeshStandardMaterial({ color: 0x22aa44, roughness: 0.5 });
    const yellowMat = new THREE.MeshStandardMaterial({ color: 0xddaa00, roughness: 0.6 });
    const ceilingMat = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.9 });
    const ceilingTileMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.85 });
    const cableMat = new THREE.MeshStandardMaterial({ color: 0x111111, roughness: 0.9 });
    const chairMat = new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.7 });
    const bookMats = [
      new THREE.MeshStandardMaterial({ color: 0x884422, roughness: 0.8 }),
      new THREE.MeshStandardMaterial({ color: 0x224488, roughness: 0.8 }),
      new THREE.MeshStandardMaterial({ color: 0x228844, roughness: 0.8 }),
      new THREE.MeshStandardMaterial({ color: 0x882244, roughness: 0.8 }),
      new THREE.MeshStandardMaterial({ color: 0x888822, roughness: 0.8 }),
    ];
    const chemicalMats = [
      new THREE.MeshStandardMaterial({ color: 0x44ff44, roughness: 0.2, transparent: true, opacity: 0.6, emissive: 0x22aa22, emissiveIntensity: 0.3 }),
      new THREE.MeshStandardMaterial({ color: 0xff4444, roughness: 0.2, transparent: true, opacity: 0.6 }),
      new THREE.MeshStandardMaterial({ color: 0x4444ff, roughness: 0.2, transparent: true, opacity: 0.6 }),
      new THREE.MeshStandardMaterial({ color: 0xffff44, roughness: 0.2, transparent: true, opacity: 0.5 }),
    ];

    // =============================================
    // FLOOR - Tiled pattern with cracked tiles
    // =============================================
    // Base floor
    this.addMesh(
      new THREE.PlaneGeometry(30, 30),
      floorMat,
      [0, 0, 0],
      colliders, objects,
      [-Math.PI / 2, 0, 0],
      false
    );

    // Tile grid overlay
    for (let tx = -14; tx <= 14; tx += 2) {
      for (let tz = -14; tz <= 14; tz += 2) {
        const isCracked = Math.random() < 0.08;
        const tileMaterial = isCracked
          ? floorTileCracked
          : ((tx + tz) % 4 === 0 ? floorTileLight : floorTileDark);
        this.addMesh(
          new THREE.PlaneGeometry(1.9, 1.9),
          tileMaterial,
          [tx, 0.005, tz],
          colliders, objects,
          [-Math.PI / 2, 0, 0],
          false
        );
        // Cracked tile: add a small displaced shard
        if (isCracked) {
          this.addMesh(
            new THREE.PlaneGeometry(0.6, 0.4),
            floorTileCracked,
            [tx + 0.3, 0.008, tz - 0.2],
            colliders, objects,
            [-Math.PI / 2, Math.random() * 0.5, 0],
            false
          );
        }
      }
    }

    // =============================================
    // CEILING - with exposed sections
    // =============================================
    this.addMesh(
      new THREE.PlaneGeometry(30, 30),
      ceilingMat,
      [0, 4, 0],
      colliders, objects,
      [Math.PI / 2, 0, 0],
      false
    );

    // Ceiling tile panels (some missing)
    for (let cx = -12; cx <= 12; cx += 3) {
      for (let cz = -12; cz <= 12; cz += 3) {
        if (Math.random() < 0.15) continue; // Missing tile
        this.addMesh(
          new THREE.BoxGeometry(2.8, 0.05, 2.8),
          ceilingTileMat,
          [cx, 3.95, cz],
          colliders, objects,
          undefined,
          false
        );
      }
    }

    // Exposed ceiling pipes (run along X axis at various Z positions)
    const ceilingPipeZ = [-10, -4, 3, 9];
    for (const cpz of ceilingPipeZ) {
      this.addMesh(
        new THREE.CylinderGeometry(0.08, 0.08, 28, 8),
        Math.random() < 0.3 ? rustPipeMat : pipeMat,
        [0, 3.85, cpz],
        colliders, objects,
        [0, 0, Math.PI / 2],
        false
      );
    }

    // Hanging cables from ceiling
    const cablePositions: [number, number, number][] = [
      [-8, 3.2, -6], [4, 2.8, -11], [10, 3.0, 7], [-11, 3.4, 11], [2, 3.1, 3],
    ];
    for (const cp of cablePositions) {
      const cableLen = 4 - cp[1] + Math.random() * 0.5;
      this.addMesh(
        new THREE.CylinderGeometry(0.015, 0.015, cableLen, 4),
        cableMat,
        [cp[0], cp[1] + cableLen / 2, cp[2]],
        colliders, objects,
        undefined,
        false
      );
    }

    // =============================================
    // OUTER WALLS
    // =============================================
    const wallConfig = [
      { pos: [0, 2, -15] as [number, number, number], size: [30, 4, 0.5] as [number, number, number] },
      { pos: [0, 2, 15] as [number, number, number], size: [30, 4, 0.5] as [number, number, number] },
      { pos: [-15, 2, 0] as [number, number, number], size: [0.5, 4, 30] as [number, number, number] },
      { pos: [15, 2, 0] as [number, number, number], size: [0.5, 4, 30] as [number, number, number] },
    ];
    for (const w of wallConfig) {
      this.addMesh(new THREE.BoxGeometry(...w.size), wallMat, w.pos, colliders, objects);
    }

    // Wall panels (decorative insets on outer walls)
    const panelPositions: { pos: [number, number, number]; rot?: [number, number, number] }[] = [
      // North wall panels
      { pos: [-10, 2, -14.65] }, { pos: [-5, 2, -14.65] }, { pos: [5, 2, -14.65] }, { pos: [10, 2, -14.65] },
      // South wall panels
      { pos: [-10, 2, 14.65] }, { pos: [5, 2, 14.65] }, { pos: [10, 2, 14.65] },
      // West wall panels
      { pos: [-14.65, 2, -10], rot: [0, Math.PI / 2, 0] },
      { pos: [-14.65, 2, 0], rot: [0, Math.PI / 2, 0] },
      { pos: [-14.65, 2, 10], rot: [0, Math.PI / 2, 0] },
      // East wall panels
      { pos: [14.65, 2, -10], rot: [0, Math.PI / 2, 0] },
      { pos: [14.65, 2, 5], rot: [0, Math.PI / 2, 0] },
    ];
    for (const p of panelPositions) {
      this.addMesh(
        new THREE.BoxGeometry(3, 2.5, 0.05),
        wallPanelMat,
        p.pos,
        colliders, objects,
        p.rot,
        false
      );
    }

    // Pipes running along walls (horizontal, near ceiling)
    // North wall pipes
    this.addMesh(
      new THREE.CylinderGeometry(0.06, 0.06, 28, 8),
      pipeMat, [0, 3.5, -14.5], colliders, objects, [0, 0, Math.PI / 2], false
    );
    this.addMesh(
      new THREE.CylinderGeometry(0.04, 0.04, 28, 8),
      rustPipeMat, [0, 3.2, -14.6], colliders, objects, [0, 0, Math.PI / 2], false
    );
    // South wall pipes
    this.addMesh(
      new THREE.CylinderGeometry(0.06, 0.06, 28, 8),
      pipeMat, [0, 3.5, 14.5], colliders, objects, [0, 0, Math.PI / 2], false
    );
    // West wall vertical pipe
    this.addMesh(
      new THREE.CylinderGeometry(0.05, 0.05, 4, 8),
      rustPipeMat, [-14.6, 2, -7], colliders, objects, undefined, false
    );
    // East wall vertical pipe
    this.addMesh(
      new THREE.CylinderGeometry(0.05, 0.05, 4, 8),
      pipeMat, [14.6, 2, 4], colliders, objects, undefined, false
    );

    // Ventilation grates on walls
    const ventPositions: { pos: [number, number, number]; rot?: [number, number, number] }[] = [
      { pos: [-14.7, 3, 5], rot: [0, Math.PI / 2, 0] },
      { pos: [14.7, 3, -5], rot: [0, Math.PI / 2, 0] },
      { pos: [7, 3, -14.7] },
      { pos: [-3, 3, 14.7] },
    ];
    for (const v of ventPositions) {
      // Vent frame
      this.addMesh(
        new THREE.BoxGeometry(1.0, 0.6, 0.05),
        darkMetalMat, v.pos, colliders, objects, v.rot, false
      );
      // Vent slats (3 horizontal bars)
      for (let s = -0.15; s <= 0.15; s += 0.15) {
        const slatPos: [number, number, number] = [v.pos[0], v.pos[1] + s, v.pos[2]];
        this.addMesh(
          new THREE.BoxGeometry(0.8, 0.03, 0.06),
          metalMat, slatPos, colliders, objects, v.rot, false
        );
      }
    }

    // Broken windows (on north and east walls)
    const windowPositions: { pos: [number, number, number]; rot?: [number, number, number]; broken: boolean }[] = [
      { pos: [0, 2.5, -14.65], broken: false },
      { pos: [-7, 2.5, -14.65], broken: true },
      { pos: [14.65, 2.5, -3], rot: [0, Math.PI / 2, 0], broken: true },
      { pos: [14.65, 2.5, 8], rot: [0, Math.PI / 2, 0], broken: false },
    ];
    for (const win of windowPositions) {
      if (win.broken) {
        // Broken window: partial glass pane (upper portion)
        const upperPos: [number, number, number] = [win.pos[0], win.pos[1] + 0.4, win.pos[2]];
        this.addMesh(
          new THREE.BoxGeometry(1.6, 0.8, 0.03),
          brokenGlassMat, upperPos, colliders, objects, win.rot, false
        );
        // Glass shards on floor below
        for (let sh = 0; sh < 3; sh++) {
          const shardX = win.pos[0] + (Math.random() - 0.5) * 1.5;
          const shardZ = win.pos[2] + (win.rot ? (Math.random() * 1.5 - 0.75) : (Math.random() * 1.0 + 0.3));
          this.addMesh(
            new THREE.PlaneGeometry(0.15 + Math.random() * 0.15, 0.1 + Math.random() * 0.1),
            brokenGlassMat,
            [shardX, 0.01, shardZ],
            colliders, objects,
            [-Math.PI / 2, Math.random() * Math.PI, 0],
            false
          );
        }
      } else {
        // Intact window
        this.addMesh(
          new THREE.BoxGeometry(1.6, 1.6, 0.03),
          glassMat, win.pos, colliders, objects, win.rot, false
        );
      }
      // Window frame
      this.addMesh(
        new THREE.BoxGeometry(1.8, 0.06, 0.08),
        darkMetalMat,
        [win.pos[0], win.pos[1] + 0.85, win.pos[2]],
        colliders, objects, win.rot, false
      );
      this.addMesh(
        new THREE.BoxGeometry(1.8, 0.06, 0.08),
        darkMetalMat,
        [win.pos[0], win.pos[1] - 0.85, win.pos[2]],
        colliders, objects, win.rot, false
      );
    }

    // =============================================
    // =============================================
    // 3-ROOM LAYOUT
    // Room A (top-left): Main Lab  -15~0 x -15~-2
    // Room B (top-right): Server Room  0~15 x -15~-2
    // Room C (bottom): Storage/Corridor  -15~15 x 2~15
    // Corridor connecting: -15~15 x -2~2
    // =============================================

    // Wall dividing Room A and Room B (vertical wall at x=0, from z=-15 to z=-2)
    // With doorway gap at z=-8 to z=-6
    this.addMesh(new THREE.BoxGeometry(0.3, 4, 6), wallMat, [0, 2, -12] as [number,number,number], colliders, objects); // z=-15 to z=-9
    this.addMesh(new THREE.BoxGeometry(0.3, 4, 5), wallMat, [0, 2, -4.5] as [number,number,number], colliders, objects); // z=-7 to z=-2

    // Wall dividing corridor from Room C (horizontal wall at z=2)
    // With doorway gap at x=-2 to x=2
    this.addMesh(new THREE.BoxGeometry(11, 4, 0.3), wallMat, [-9.5, 2, 2] as [number,number,number], colliders, objects); // left section
    this.addMesh(new THREE.BoxGeometry(11, 4, 0.3), wallMat, [9.5, 2, 2] as [number,number,number], colliders, objects); // right section

    // Wall dividing corridor from rooms A&B (horizontal wall at z=-2)
    // With doorway gaps at x=-7 and x=7
    this.addMesh(new THREE.BoxGeometry(4, 4, 0.3), wallMat, [-13, 2, -2] as [number,number,number], colliders, objects); // far left
    this.addMesh(new THREE.BoxGeometry(8, 4, 0.3), wallMat, [-3, 2, -2] as [number,number,number], colliders, objects); // center-left
    this.addMesh(new THREE.BoxGeometry(8, 4, 0.3), wallMat, [3, 2, -2] as [number,number,number], colliders, objects); // center-right
    this.addMesh(new THREE.BoxGeometry(4, 4, 0.3), wallMat, [13, 2, -2] as [number,number,number], colliders, objects); // far right

    // Door frames at doorways
    const doorFrameMat = darkMetalMat;
    const doorways: [number,number,number][] = [
      [0, 1.1, -7], // A↔B doorway
      [-7, 1.1, -2], // A→corridor doorway
      [7, 1.1, -2],  // B→corridor doorway
      [-2, 1.1, 2],  // corridor→C left
      [2, 1.1, 2],   // corridor→C right
    ];
    for (const dp of doorways) {
      // Left frame
      this.addMesh(new THREE.BoxGeometry(0.08, 2.2, 0.3), doorFrameMat,
        [dp[0] - 0.9, dp[1], dp[2]], colliders, objects, undefined, false);
      // Right frame
      this.addMesh(new THREE.BoxGeometry(0.08, 2.2, 0.3), doorFrameMat,
        [dp[0] + 0.9, dp[1], dp[2]], colliders, objects, undefined, false);
      // Top frame
      this.addMesh(new THREE.BoxGeometry(1.88, 0.1, 0.3), doorFrameMat,
        [dp[0], 2.2, dp[2]], colliders, objects, undefined, false);
    }

    // Room labels (small signs next to doors)
    const signMat = new THREE.MeshStandardMaterial({ color: 0x2244aa, emissive: 0x1122aa, emissiveIntensity: 0.3, roughness: 0.5 });
    this.addMesh(new THREE.BoxGeometry(0.6, 0.2, 0.02), signMat, [-8.5, 2.5, -2.15] as [number,number,number], colliders, objects, undefined, false);
    this.addMesh(new THREE.BoxGeometry(0.6, 0.2, 0.02), signMat, [8.5, 2.5, -2.15] as [number,number,number], colliders, objects, undefined, false);
    this.addMesh(new THREE.BoxGeometry(0.6, 0.2, 0.02), signMat, [0, 2.5, 2.15] as [number,number,number], colliders, objects, undefined, false);

    // =============================================
    // LAB DESKS (with legs)
    // =============================================
    const deskPositions: { pos: [number, number, number]; rot?: number }[] = [
      { pos: [-3, 0, -8] }, { pos: [3, 0, -8] }, { pos: [-8, 0, -10] },
      { pos: [8, 0, -3] }, { pos: [-12, 0, 8] }, { pos: [12, 0, 8] },
      { pos: [-3, 0, 10] }, { pos: [7, 0, 10] },
    ];
    for (const desk of deskPositions) {
      const dx = desk.pos[0];
      const dz = desk.pos[2];
      // Tabletop
      this.addMesh(
        new THREE.BoxGeometry(2, 0.08, 0.9),
        labMat, [dx, 0.9, dz], colliders, objects
      );
      // Four legs
      const legOffsets = [[-0.85, -0.35], [-0.85, 0.35], [0.85, -0.35], [0.85, 0.35]];
      for (const [lx, lz] of legOffsets) {
        this.addMesh(
          new THREE.BoxGeometry(0.06, 0.9, 0.06),
          metalMat, [dx + lx, 0.45, dz + lz], colliders, objects, undefined, false
        );
      }
    }

    // =============================================
    // COMPUTER TERMINALS (monitor + keyboard)
    // =============================================
    const terminalPositions: { pos: [number, number, number]; rot?: number; on: boolean }[] = [
      { pos: [-3, 0.94, -8], on: true },
      { pos: [3, 0.94, -8], on: false },
      { pos: [-8, 0.94, -10], on: true },
      { pos: [12, 0.94, 8], on: false },
      { pos: [-3, 0.94, 10], on: true },
    ];
    for (const term of terminalPositions) {
      const tx = term.pos[0];
      const ty = term.pos[1];
      const tz = term.pos[2];
      // Monitor back/housing
      this.addMesh(
        new THREE.BoxGeometry(0.5, 0.4, 0.05),
        darkMetalMat, [tx, ty + 0.3, tz - 0.15], colliders, objects, undefined, false
      );
      // Monitor screen
      this.addMesh(
        new THREE.BoxGeometry(0.45, 0.32, 0.01),
        term.on ? screenMat : screenOffMat,
        [tx, ty + 0.3, tz - 0.18], colliders, objects, undefined, false
      );
      // Monitor stand
      this.addMesh(
        new THREE.CylinderGeometry(0.04, 0.06, 0.15, 8),
        metalMat, [tx, ty + 0.07, tz - 0.15], colliders, objects, undefined, false
      );
      // Monitor base
      this.addMesh(
        new THREE.CylinderGeometry(0.1, 0.1, 0.02, 12),
        metalMat, [tx, ty + 0.01, tz - 0.15], colliders, objects, undefined, false
      );
      // Keyboard
      this.addMesh(
        new THREE.BoxGeometry(0.35, 0.015, 0.12),
        new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.6 }),
        [tx, ty + 0.01, tz + 0.1], colliders, objects, undefined, false
      );
      // Screen glow for active terminals
      if (term.on) {
        const glow = new THREE.PointLight(0x22ff66, 0.08, 2);
        glow.position.set(tx, ty + 0.3, tz - 0.3);
        glow.userData.isMapObject = true;
        this.scene.add(glow);
        objects.push(glow);
      }
    }

    // =============================================
    // MICROSCOPES (cylinder stacks)
    // =============================================
    const microscopePositions: [number, number, number][] = [
      [3.5, 0.94, -8], [-2.5, 0.94, 10], [7.5, 0.94, 10],
    ];
    for (const mp of microscopePositions) {
      // Base
      this.addMesh(
        new THREE.CylinderGeometry(0.08, 0.1, 0.04, 12),
        darkMetalMat, [mp[0], mp[1] + 0.02, mp[2]], colliders, objects, undefined, false
      );
      // Main column
      this.addMesh(
        new THREE.CylinderGeometry(0.025, 0.03, 0.35, 8),
        metalMat, [mp[0], mp[1] + 0.2, mp[2]], colliders, objects, undefined, false
      );
      // Eyepiece (angled cylinder)
      this.addMesh(
        new THREE.CylinderGeometry(0.02, 0.025, 0.12, 8),
        darkMetalMat, [mp[0], mp[1] + 0.38, mp[2] - 0.02],
        colliders, objects, [0.4, 0, 0], false
      );
      // Objective lens
      this.addMesh(
        new THREE.CylinderGeometry(0.015, 0.02, 0.06, 8),
        metalMat, [mp[0], mp[1] + 0.06, mp[2] + 0.03],
        colliders, objects, undefined, false
      );
    }

    // =============================================
    // CHEMICAL CONTAINERS & BROKEN GLASS TUBES
    // =============================================
    // Beakers/flasks on desks
    const beakerPositions: [number, number, number][] = [
      [-3.6, 0.94, -7.8], [-8.3, 0.94, -10.2], [8.4, 0.94, -2.8],
      [-12.4, 0.94, 8.2], [7.3, 0.94, 10.3],
    ];
    for (const bp of beakerPositions) {
      const chemMat = chemicalMats[Math.floor(Math.random() * chemicalMats.length)];
      // Glass container (cylinder)
      this.addMesh(
        new THREE.CylinderGeometry(0.04, 0.05, 0.15, 10),
        glassMat, [bp[0], bp[1] + 0.08, bp[2]], colliders, objects, undefined, false
      );
      // Liquid inside (slightly smaller)
      this.addMesh(
        new THREE.CylinderGeometry(0.035, 0.045, 0.08 + Math.random() * 0.05, 10),
        chemMat, [bp[0], bp[1] + 0.05, bp[2]], colliders, objects, undefined, false
      );
    }

    // Broken glass tubes on the floor
    const brokenTubePositions: [number, number, number][] = [
      [-2, 0.02, -6], [6, 0.02, -4], [-7, 0.02, 3], [1, 0.02, 8],
    ];
    for (const bt of brokenTubePositions) {
      // Broken tube (lying on side)
      this.addMesh(
        new THREE.CylinderGeometry(0.03, 0.03, 0.2, 8, 1, true),
        brokenGlassMat, bt, colliders, objects,
        [0, Math.random() * Math.PI, Math.PI / 2], false
      );
      // Spilled chemical puddle
      this.addMesh(
        new THREE.PlaneGeometry(0.3 + Math.random() * 0.2, 0.25 + Math.random() * 0.15),
        chemicalMats[Math.floor(Math.random() * chemicalMats.length)],
        [bt[0] + 0.1, 0.003, bt[2]],
        colliders, objects,
        [-Math.PI / 2, Math.random() * Math.PI, 0], false
      );
    }

    // Larger chemical containers (on floor)
    const containerPositions: [number, number, number][] = [
      [-13, 0.3, -13], [13, 0.3, -12], [-12, 0.3, 12],
    ];
    for (const cp of containerPositions) {
      this.addMesh(
        new THREE.CylinderGeometry(0.2, 0.2, 0.6, 12),
        new THREE.MeshStandardMaterial({
          color: 0x336633, roughness: 0.5, metalness: 0.3,
        }),
        cp, colliders, objects
      );
      // Hazard label
      this.addMesh(
        new THREE.PlaneGeometry(0.15, 0.15),
        yellowMat,
        [cp[0], cp[1] + 0.05, cp[2] + 0.21],
        colliders, objects, undefined, false
      );
    }

    // =============================================
    // OFFICE CHAIRS (cylinder base + seat)
    // =============================================
    const chairPositions: { pos: [number, number, number]; rot: number }[] = [
      { pos: [-3, 0, -7.4], rot: 0.3 },
      { pos: [3, 0, -7.4], rot: -0.5 },
      { pos: [-8, 0, -9.4], rot: 0.8 },
      { pos: [12, 0, 7.4], rot: 2.1 },
      { pos: [-3, 0, 10.6], rot: -0.2 },
      // Overturned chair
      { pos: [9, 0, -2], rot: 1.5 },
    ];
    for (let ci = 0; ci < chairPositions.length; ci++) {
      const ch = chairPositions[ci];
      const cx = ch.pos[0], cz = ch.pos[2];
      const isOverturned = ci === chairPositions.length - 1;

      if (isOverturned) {
        // Overturned chair on its side
        // Base cylinder (on side)
        this.addMesh(
          new THREE.CylinderGeometry(0.25, 0.25, 0.05, 12),
          darkMetalMat, [cx, 0.25, cz], colliders, objects, [0, 0, Math.PI / 2], false
        );
        // Seat on its side
        this.addMesh(
          new THREE.BoxGeometry(0.4, 0.04, 0.4),
          chairMat, [cx, 0.15, cz], colliders, objects, [0, ch.rot, Math.PI / 2], false
        );
      } else {
        // Chair base (5-star base approximation)
        this.addMesh(
          new THREE.CylinderGeometry(0.25, 0.25, 0.03, 12),
          darkMetalMat, [cx, 0.02, cz], colliders, objects, undefined, false
        );
        // Chair stem
        this.addMesh(
          new THREE.CylinderGeometry(0.03, 0.03, 0.35, 8),
          metalMat, [cx, 0.2, cz], colliders, objects, undefined, false
        );
        // Seat
        this.addMesh(
          new THREE.BoxGeometry(0.4, 0.05, 0.4),
          chairMat, [cx, 0.4, cz], colliders, objects, [0, ch.rot, 0], false
        );
        // Backrest
        this.addMesh(
          new THREE.BoxGeometry(0.38, 0.35, 0.04),
          chairMat, [cx - Math.sin(ch.rot) * 0.2, 0.6, cz - Math.cos(ch.rot) * 0.2],
          colliders, objects, [0, ch.rot, 0], false
        );
      }
    }

    // =============================================
    // FILING CABINETS
    // =============================================
    const cabinetPositions: { pos: [number, number, number]; rot?: number }[] = [
      { pos: [-14.2, 0.6, -8] },
      { pos: [-14.2, 0.6, -6] },
      { pos: [14.2, 0.6, 2], rot: Math.PI },
      { pos: [14.2, 0.6, 4], rot: Math.PI },
      { pos: [-9, 0.6, -14.2], rot: Math.PI / 2 },
    ];
    for (const cab of cabinetPositions) {
      // Cabinet body
      this.addMesh(
        new THREE.BoxGeometry(0.5, 1.2, 0.6),
        metalMat, cab.pos, colliders, objects, cab.rot ? [0, cab.rot, 0] : undefined
      );
      // Drawer handles (3 drawers)
      for (let d = -0.35; d <= 0.35; d += 0.35) {
        this.addMesh(
          new THREE.BoxGeometry(0.15, 0.02, 0.02),
          darkMetalMat,
          [cab.pos[0], cab.pos[1] + d, cab.pos[2] + 0.31],
          colliders, objects, cab.rot ? [0, cab.rot, 0] : undefined, false
        );
      }
    }

    // =============================================
    // BOOKSHELVES WITH BOOKS
    // =============================================
    const shelfPositions: { pos: [number, number, number]; rot?: number }[] = [
      { pos: [-14, 1.2, 3] },
      { pos: [-14, 1.2, 6] },
      { pos: [6, 1.2, 14.2], rot: Math.PI / 2 },
    ];
    for (const shelf of shelfPositions) {
      const sx = shelf.pos[0], sz = shelf.pos[2];
      // Shelf frame (back)
      this.addMesh(
        new THREE.BoxGeometry(1.2, 2.2, 0.05),
        new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 0.85 }),
        [sx, shelf.pos[1], sz], colliders, objects,
        shelf.rot ? [0, shelf.rot, 0] : undefined
      );
      // Shelf boards (3 shelves)
      for (let sh = -0.7; sh <= 0.7; sh += 0.7) {
        this.addMesh(
          new THREE.BoxGeometry(1.15, 0.03, 0.3),
          new THREE.MeshStandardMaterial({ color: 0x3a2a1a, roughness: 0.85 }),
          [sx, shelf.pos[1] + sh, sz + 0.15],
          colliders, objects, shelf.rot ? [0, shelf.rot, 0] : undefined, false
        );
      }
      // Books on shelves (random arrangement)
      for (let sh = -0.7; sh <= 0.7; sh += 0.7) {
        const bookCount = 3 + Math.floor(Math.random() * 4);
        for (let b = 0; b < bookCount; b++) {
          const bookH = 0.15 + Math.random() * 0.1;
          const bookW = 0.03 + Math.random() * 0.03;
          const bx = sx - 0.4 + b * 0.12 + Math.random() * 0.04;
          this.addMesh(
            new THREE.BoxGeometry(bookW, bookH, 0.18),
            bookMats[Math.floor(Math.random() * bookMats.length)],
            [bx, shelf.pos[1] + sh + 0.02 + bookH / 2, sz + 0.12],
            colliders, objects, shelf.rot ? [0, shelf.rot, 0] : undefined, false
          );
        }
      }
    }

    // =============================================
    // BARREL OBSTACLES (biohazard)
    // =============================================
    const barrels: [number, number, number][] = [
      [-1, 0.5, 0], [2, 0.5, -2], [-6, 0.5, 3], [6, 0.5, -1],
      [-11, 0.5, 12], [11, 0.5, 12],
    ];
    for (const pos of barrels) {
      this.addMesh(
        new THREE.CylinderGeometry(0.35, 0.35, 1, 12),
        new THREE.MeshStandardMaterial({ color: 0x886633, roughness: 0.7 }),
        pos, colliders, objects
      );
      // Hazard stripe on barrel
      this.addMesh(
        new THREE.CylinderGeometry(0.36, 0.36, 0.1, 12),
        yellowMat,
        [pos[0], pos[1] + 0.2, pos[2]],
        colliders, objects, undefined, false
      );
    }

    // =============================================
    // BLOOD STAINS on floor
    // =============================================
    const bloodPositions: { pos: [number, number, number]; size: [number, number] }[] = [
      { pos: [-4, 0.004, -3], size: [1.2, 0.8] },
      { pos: [6, 0.004, -6], size: [0.6, 0.9] },
      { pos: [-9, 0.004, 7], size: [1.5, 1.0] },
      { pos: [3, 0.004, 3], size: [0.8, 0.6] },
      { pos: [10, 0.004, -8], size: [1.0, 1.2] },
      { pos: [-1, 0.004, 12], size: [0.7, 0.5] },
      { pos: [-12, 0.004, -4], size: [0.9, 0.7] },
      { pos: [0, 0.004, -2], size: [1.8, 1.4] }, // Large pool near center
    ];
    for (const b of bloodPositions) {
      this.addMesh(
        new THREE.PlaneGeometry(b.size[0], b.size[1]),
        bloodMat, b.pos, colliders, objects,
        [-Math.PI / 2, Math.random() * Math.PI * 2, 0], false
      );
    }

    // Drag marks (elongated blood trails)
    this.addMesh(
      new THREE.PlaneGeometry(0.3, 3.0),
      bloodMat, [0.5, 0.004, -3.5], colliders, objects,
      [-Math.PI / 2, 0.4, 0], false
    );
    this.addMesh(
      new THREE.PlaneGeometry(0.2, 2.5),
      bloodMat, [8, 0.004, -6], colliders, objects,
      [-Math.PI / 2, 1.2, 0], false
    );

    // =============================================
    // EMERGENCY LIGHTS (more of them, with fixtures)
    // =============================================
    const emergencyPositions: [number, number, number][] = [
      [-13, 3.5, -13], [13, 3.5, -13], [-13, 3.5, 13], [13, 3.5, 13],
      [0, 3.5, 0], [-7, 3.5, 0], [7, 3.5, 0],
      [0, 3.5, -10], [0, 3.5, 10],
      [-13, 3.5, 0], [13, 3.5, 0],
    ];
    for (const pos of emergencyPositions) {
      const light = new THREE.PointLight(0xff2200, 0.3, 8);
      light.position.set(pos[0], pos[1], pos[2]);
      light.userData.isMapObject = true;
      this.scene.add(light);
      objects.push(light);

      // Light fixture housing
      this.addMesh(
        new THREE.BoxGeometry(0.3, 0.06, 0.3),
        darkMetalMat, [pos[0], pos[1] + 0.35, pos[2]],
        colliders, objects, undefined, false
      );
      // Light bulb
      this.addMesh(
        new THREE.SphereGeometry(0.06, 8, 6),
        new THREE.MeshStandardMaterial({
          color: 0xff2200, emissive: 0xff2200, emissiveIntensity: 0.8,
        }),
        [pos[0], pos[1] + 0.25, pos[2]],
        colliders, objects, undefined, false
      );
    }

    // =============================================
    // SPARKING ELECTRICAL PANELS
    // =============================================
    const sparkPositions: { pos: [number, number, number]; rot?: [number, number, number] }[] = [
      { pos: [-14.6, 1.8, -2], rot: [0, Math.PI / 2, 0] },
      { pos: [14.6, 1.8, 10], rot: [0, Math.PI / 2, 0] },
      { pos: [4, 1.8, -14.6] },
    ];
    for (const sp of sparkPositions) {
      // Panel box
      this.addMesh(
        new THREE.BoxGeometry(0.8, 1.0, 0.12),
        metalMat, sp.pos, colliders, objects, sp.rot, false
      );
      // Panel door (slightly open)
      this.addMesh(
        new THREE.BoxGeometry(0.35, 0.9, 0.02),
        darkMetalMat,
        [sp.pos[0] + (sp.rot ? 0 : 0.25), sp.pos[1], sp.pos[2] + (sp.rot ? 0.25 : 0)],
        colliders, objects,
        sp.rot ? [0, sp.rot[1] + 0.4, 0] : [0, 0.4, 0],
        false
      );
      // Spark light (flickering yellow-white point light)
      const sparkLight = new THREE.PointLight(0xffffaa, 0.5, 3);
      sparkLight.position.set(sp.pos[0], sp.pos[1], sp.pos[2]);
      sparkLight.userData.isMapObject = true;
      sparkLight.userData.flickerSpark = true;
      this.scene.add(sparkLight);
      objects.push(sparkLight);

      // Small emissive spark geometry
      this.addMesh(
        new THREE.SphereGeometry(0.03, 4, 4),
        new THREE.MeshStandardMaterial({
          color: 0xffff00, emissive: 0xffaa00, emissiveIntensity: 2.0,
        }),
        [sp.pos[0], sp.pos[1] - 0.1, sp.pos[2] + 0.08],
        colliders, objects, undefined, false
      );
    }

    // =============================================
    // OVERTURNED FURNITURE
    // =============================================
    // Overturned desk
    this.addMesh(
      new THREE.BoxGeometry(2, 0.08, 0.9),
      labMat, [8, 0.45, -8], colliders, objects, [0, 0.2, Math.PI / 2]
    );
    // Desk legs (sticking out)
    this.addMesh(
      new THREE.BoxGeometry(0.06, 0.9, 0.06),
      metalMat, [8, 0.7, -8.3], colliders, objects, [0, 0, Math.PI / 2], false
    );

    // Overturned filing cabinet
    this.addMesh(
      new THREE.BoxGeometry(0.5, 1.2, 0.6),
      metalMat, [2, 0.3, 7], colliders, objects, [0, 0.5, Math.PI / 2]
    );

    // =============================================
    // FIRE EXTINGUISHERS
    // =============================================
    const extPositions: { pos: [number, number, number]; wallRot?: number }[] = [
      { pos: [-14.5, 0.9, -11] },
      { pos: [14.5, 0.9, -7] },
      { pos: [-6, 0.9, 4.7] },  // Near internal wall
    ];
    for (const ext of extPositions) {
      // Cylinder body
      this.addMesh(
        new THREE.CylinderGeometry(0.08, 0.08, 0.4, 10),
        redMat, ext.pos, colliders, objects, undefined, false
      );
      // Nozzle
      this.addMesh(
        new THREE.CylinderGeometry(0.01, 0.02, 0.08, 6),
        darkMetalMat,
        [ext.pos[0], ext.pos[1] + 0.24, ext.pos[2]],
        colliders, objects, undefined, false
      );
      // Wall mount bracket
      this.addMesh(
        new THREE.BoxGeometry(0.2, 0.04, 0.06),
        metalMat,
        [ext.pos[0], ext.pos[1] + 0.1, ext.pos[2]],
        colliders, objects, undefined, false
      );
    }

    // =============================================
    // FIRST AID KITS on walls
    // =============================================
    const firstAidPositions: { pos: [number, number, number]; rot?: [number, number, number] }[] = [
      { pos: [-14.6, 1.5, 8], rot: [0, Math.PI / 2, 0] },
      { pos: [12, 1.5, -14.6] },
    ];
    for (const fa of firstAidPositions) {
      // Kit box
      this.addMesh(
        new THREE.BoxGeometry(0.35, 0.25, 0.08),
        whiteMat, fa.pos, colliders, objects, fa.rot, false
      );
      // Red cross (horizontal bar)
      this.addMesh(
        new THREE.BoxGeometry(0.15, 0.04, 0.01),
        redMat,
        [fa.pos[0], fa.pos[1], fa.pos[2] + (fa.rot ? 0 : -0.045)],
        colliders, objects, fa.rot, false
      );
      // Red cross (vertical bar)
      this.addMesh(
        new THREE.BoxGeometry(0.04, 0.15, 0.01),
        redMat,
        [fa.pos[0], fa.pos[1], fa.pos[2] + (fa.rot ? 0 : -0.045)],
        colliders, objects, fa.rot, false
      );
    }

    // =============================================
    // SAFETY SIGNS on walls
    // =============================================
    const signPositions: { pos: [number, number, number]; rot?: [number, number, number]; color: THREE.MeshStandardMaterial }[] = [
      { pos: [-14.7, 2.8, -5], rot: [0, Math.PI / 2, 0], color: yellowMat },    // Caution
      { pos: [14.7, 2.8, -1], rot: [0, Math.PI / 2, 0], color: redMat },         // Danger
      { pos: [-8, 2.8, -14.7], color: greenMat },                                  // Exit
      { pos: [10, 2.8, 14.7], color: yellowMat },                                  // Biohazard
    ];
    for (const sign of signPositions) {
      // Sign backing
      this.addMesh(
        new THREE.BoxGeometry(0.5, 0.3, 0.02),
        sign.color, sign.pos, colliders, objects, sign.rot, false
      );
      // Sign border
      this.addMesh(
        new THREE.BoxGeometry(0.55, 0.35, 0.015),
        darkMetalMat, sign.pos, colliders, objects, sign.rot, false
      );
    }

    // =============================================
    // WALL-MOUNTED MONITORS
    // =============================================
    const wallMonitorPositions: { pos: [number, number, number]; rot?: [number, number, number]; on: boolean }[] = [
      { pos: [-14.6, 2.2, -12], rot: [0, Math.PI / 2, 0], on: false },
      { pos: [14.6, 2.2, -10], rot: [0, Math.PI / 2, 0], on: true },
      { pos: [3, 2.2, 4.8], on: false },
      { pos: [-3, 2.2, 4.8], on: true },
    ];
    for (const wm of wallMonitorPositions) {
      // Monitor body
      this.addMesh(
        new THREE.BoxGeometry(0.8, 0.5, 0.05),
        darkMetalMat, wm.pos, colliders, objects, wm.rot, false
      );
      // Screen surface
      const screenOffset: [number, number, number] = wm.rot
        ? [wm.pos[0] + 0.03, wm.pos[1], wm.pos[2]]
        : [wm.pos[0], wm.pos[1], wm.pos[2] + 0.03];
      this.addMesh(
        new THREE.BoxGeometry(0.7, 0.42, 0.01),
        wm.on ? screenMat : screenOffMat,
        screenOffset, colliders, objects, wm.rot, false
      );
      // Mount bracket
      this.addMesh(
        new THREE.BoxGeometry(0.08, 0.08, 0.06),
        metalMat,
        [wm.pos[0], wm.pos[1], wm.pos[2]],
        colliders, objects, wm.rot, false
      );
      // Glow for active monitors
      if (wm.on) {
        const monGlow = new THREE.PointLight(0x22ff66, 0.06, 2.5);
        monGlow.position.set(
          wm.rot ? wm.pos[0] + 0.2 : wm.pos[0],
          wm.pos[1],
          wm.rot ? wm.pos[2] : wm.pos[2] + 0.2
        );
        monGlow.userData.isMapObject = true;
        this.scene.add(monGlow);
        objects.push(monGlow);
      }
    }

    // =============================================
    // LAB EQUIPMENT (large standing instruments)
    // =============================================
    const standingEquipment: [number, number, number][] = [
      [-7, 0.8, -12], [7, 0.8, -12], [-12, 0.8, -5], [12, 0.8, -5],
      [0, 0.8, -12], [-8, 0.8, 5], [8, 0.8, 5],
    ];
    for (const pos of standingEquipment) {
      // Main column
      this.addMesh(
        new THREE.CylinderGeometry(0.25, 0.3, 1.6, 10),
        metalMat, pos, colliders, objects
      );
      // Top cap
      this.addMesh(
        new THREE.CylinderGeometry(0.28, 0.25, 0.08, 10),
        darkMetalMat,
        [pos[0], pos[1] + 0.84, pos[2]],
        colliders, objects, undefined, false
      );
      // Small indicator light on equipment
      this.addMesh(
        new THREE.SphereGeometry(0.03, 6, 6),
        new THREE.MeshStandardMaterial({
          color: Math.random() > 0.5 ? 0x00ff00 : 0xff0000,
          emissive: Math.random() > 0.5 ? 0x00ff00 : 0xff0000,
          emissiveIntensity: 0.6,
        }),
        [pos[0] + 0.2, pos[1] + 0.5, pos[2]],
        colliders, objects, undefined, false
      );
    }

    // =============================================
    // AMBIENT FOG LIGHT (dim blue)
    // =============================================
    const ambientFill = new THREE.PointLight(0x112244, 0.1, 30);
    ambientFill.position.set(0, 3, 0);
    ambientFill.userData.isMapObject = true;
    this.scene.add(ambientFill);
    objects.push(ambientFill);

    return {
      colliders,
      objects,
      playerSpawn: new THREE.Vector3(0, PLAYER_HEIGHT, 0),
      zombieSpawnArea: {
        min: new THREE.Vector3(-13, 0, -13),
        max: new THREE.Vector3(13, 0, 13),
      },
    };
  }

  private buildCity(): MapResult {
    const colliders: THREE.Box3[] = [];
    const objects: THREE.Object3D[] = [];

    const M = (c: number, r = 0.8, m = 0) =>
      new THREE.MeshStandardMaterial({ color: c, roughness: r, metalness: m });

    const asphalt = M(0x2a2a2a, 0.95);
    const sidewalk = M(0x666666, 0.85);
    const buildingBase = M(0x555560, 0.75, 0.1);

    // === SKY / AMBIENT ===
    // Hemisphere light (sky blue + ground bounce)
    const hemi = new THREE.HemisphereLight(0x6688aa, 0x443322, 1.2);
    hemi.userData.isMapObject = true;
    this.scene.add(hemi);
    objects.push(hemi);

    // Directional sun (angled, warm evening light)
    const sun = new THREE.DirectionalLight(0xffeedd, 1.5);
    sun.position.set(20, 15, 10);
    sun.userData.isMapObject = true;
    this.scene.add(sun);
    objects.push(sun);

    // Ambient fill
    const amb = new THREE.AmbientLight(0x556677, 0.6);
    amb.userData.isMapObject = true;
    this.scene.add(amb);
    objects.push(amb);

    // === GROUND ===
    // Main road (runs along Z axis)
    this.addMesh(new THREE.PlaneGeometry(10, 50), asphalt, [0, 0, 0], colliders, objects, [-Math.PI / 2, 0, 0], false);
    // Road center lines
    for (let z = -22; z <= 22; z += 4) {
      this.addMesh(new THREE.PlaneGeometry(0.15, 2), M(0xcccc55), [0, 0.005, z], colliders, objects, [-Math.PI / 2, 0, 0], false);
    }

    // Sidewalks (both sides)
    for (const side of [-1, 1]) {
      this.addMesh(new THREE.PlaneGeometry(5, 50), sidewalk, [side * 7.5, 0.08, 0], colliders, objects, [-Math.PI / 2, 0, 0], false);
      // Curb (no collider — player can step over)
      this.addMesh(new THREE.BoxGeometry(0.3, 0.15, 50), M(0x777777), [side * 5.1, 0.04, 0], colliders, objects, undefined, false);
    }

    // Cross street (runs along X axis at z=0)
    this.addMesh(new THREE.PlaneGeometry(30, 8), asphalt, [0, 0.001, 0], colliders, objects, [-Math.PI / 2, 0, 0], false);

    // === BUILDINGS (damaged shops along both sides) ===
    const shopColors = [0x6a5540, 0x4a5560, 0x5a4a3a, 0x5a6050, 0x604848, 0x505a60];
    const awningColors = [0x773322, 0x556622, 0x884411, 0x335544, 0x663333, 0x445566];

    for (const side of [-1, 1]) {
      for (let i = 0; i < 6; i++) {
        const z = -20 + i * 7.5;
        const x = side * 12;
        const h = 4 + Math.random() * 3;
        const w = 6;
        const d = 5;
        const color = shopColors[i % shopColors.length];
        const isBroken = Math.random() > 0.3;

        // Building body (stained, dirty)
        this.addMesh(new THREE.BoxGeometry(d, h, w), M(color, 0.85, 0.02), [x, h / 2, z], colliders, objects);

        // Shop front face (dark, damaged)
        this.addMesh(
          new THREE.PlaneGeometry(w - 0.2, 2.5),
          M(color - 0x0a0a0a, 0.75),
          [x - side * d / 2 - side * 0.01, 1.5, z],
          colliders, objects,
          [0, side > 0 ? Math.PI / 2 : -Math.PI / 2, 0],
          false
        );

        // Awning / canopy (some torn/tilted)
        const awningMat = M(awningColors[i % awningColors.length], 0.8);
        const awning = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.06, w + 0.5), awningMat);
        awning.position.set(x - side * (d / 2 + 0.5), 2.8, z);
        if (isBroken) {
          awning.rotation.z = side * (0.1 + Math.random() * 0.2); // tilted/sagging
          awning.position.y -= 0.2;
        }
        awning.userData.isMapObject = true;
        this.scene.add(awning);
        objects.push(awning);

        // Shop sign (some broken/flickering, some dark)
        const signBroken = Math.random() > 0.4;
        const signMat = new THREE.MeshStandardMaterial({
          color: signBroken ? 0x444444 : 0xddddcc,
          emissive: signBroken ? 0x000000 : 0xffeecc,
          emissiveIntensity: signBroken ? 0 : 0.2,
        });
        const sign = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.5, 2), signMat);
        sign.position.set(x - side * (d / 2 + 0.05), 3.2, z);
        if (signBroken) sign.rotation.z = (Math.random() - 0.5) * 0.3; // crooked
        sign.userData.isMapObject = true;
        this.scene.add(sign);
        objects.push(sign);

        // Windows (fewer, no glass shards for performance)
        for (let wz = -w / 2 + 1.5; wz < w / 2; wz += 3) {
          const winColor = Math.random() > 0.5 ? 0x1a1a1a : 0x334455;
          this.addMesh(
            new THREE.PlaneGeometry(1.2, 1.0),
            M(winColor, 0.5),
            [x - side * (d / 2 + 0.01), 2, z + wz],
            colliders, objects,
            [0, side > 0 ? Math.PI / 2 : -Math.PI / 2, 0],
            false
          );
        }

        // Graffiti / damage marks on walls
        if (Math.random() > 0.5) {
          const graffiti = new THREE.Mesh(
            new THREE.PlaneGeometry(1.5 + Math.random(), 0.8 + Math.random() * 0.5),
            M(0x333333 + Math.floor(Math.random() * 0x222222), 0.9)
          );
          graffiti.position.set(
            x - side * (d / 2 + 0.02),
            1.5 + Math.random() * 1.5,
            z + (Math.random() - 0.5) * 3
          );
          graffiti.rotation.y = side > 0 ? Math.PI / 2 : -Math.PI / 2;
          graffiti.userData.isMapObject = true;
          this.scene.add(graffiti);
          objects.push(graffiti);
        }

        // Scattered goods on sidewalk in front of shop
        if (Math.random() > 0.7) {
          for (let g = 0; g < 1; g++) {
            const goods = new THREE.Mesh(
              new THREE.BoxGeometry(0.2 + Math.random() * 0.2, 0.15, 0.15),
              M(0x665544 + Math.floor(Math.random() * 0x222222))
            );
            goods.position.set(
              x - side * (d / 2 + 1 + Math.random()),
              0.08,
              z + (Math.random() - 0.5) * 3
            );
            goods.rotation.y = Math.random() * Math.PI;
            goods.userData.isMapObject = true;
            this.scene.add(goods);
            objects.push(goods);
          }
        }

        // Shop light (dim, some off)
        if (!signBroken) {
          const shopLight = new THREE.PointLight(0xffeecc, 1.0, 8);
          shopLight.position.set(x - side * 3, 2.5, z);
          shopLight.userData.isMapObject = true;
          this.scene.add(shopLight);
          objects.push(shopLight);
        }
      }
    }

    // === WRECKED CARS ===
    const carColors = [0x882222, 0x333388, 0x888888, 0x228822, 0x886633];
    const carPositions: [number, number, number, number][] = [
      [-2, 0.6, -8, 0.3], [3, 0.6, 5, -0.1], [-1, 0.6, 15, 0.5],
      [2, 0.6, -15, -0.2], [-3, 0.6, -1, 1.2], [1, 0.6, 10, 0.1],
    ];
    for (let i = 0; i < carPositions.length; i++) {
      const [cx, cy, cz, rot] = carPositions[i];
      const carGroup = new THREE.Group();
      // Body
      const body = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.9, 3.5), M(carColors[i % carColors.length]));
      body.position.y = 0.5;
      carGroup.add(body);
      // Roof
      const roof = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.5, 1.8), M(carColors[i % carColors.length] - 0x111111));
      roof.position.set(0, 1.1, -0.2);
      carGroup.add(roof);
      // Wheels (4)
      for (const [wx, wz] of [[-0.9, -1.2], [0.9, -1.2], [-0.9, 1.2], [0.9, 1.2]]) {
        const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.2, 8), M(0x222222));
        wheel.rotation.z = Math.PI / 2;
        wheel.position.set(wx, 0.15, wz);
        carGroup.add(wheel);
      }
      // Headlight (flickering, one per car for performance)
      if (i < 3) {
        const headlight = new THREE.PointLight(0xffffcc, 0, 5);
        headlight.position.set(0.6, 0.4, -1.6);
        headlight.userData.isCarLight = true;
        carGroup.add(headlight);
      }

      // Smoke particles (small spheres that drift up)
      if (i < 2) { // only 2 cars smoke
        for (let s = 0; s < 2; s++) {
          const smoke = new THREE.Mesh(
            new THREE.SphereGeometry(0.15 + Math.random() * 0.2, 5, 4),
            new THREE.MeshStandardMaterial({
              color: 0x444444, transparent: true, opacity: 0.3,
            })
          );
          smoke.position.set(
            (Math.random() - 0.5) * 0.5,
            1.2 + s * 0.4 + Math.random() * 0.3,
            -1.5 + Math.random() * 0.3
          );
          smoke.scale.setScalar(0.5 + s * 0.3);
          smoke.userData.isSmoke = true;
          smoke.userData.baseY = smoke.position.y;
          smoke.userData.speed = 0.2 + Math.random() * 0.3;
          carGroup.add(smoke);
        }
      }

      // Dent/damage mark
      const dent = new THREE.Mesh(
        new THREE.SphereGeometry(0.25, 5, 4),
        new THREE.MeshStandardMaterial({ color: 0x222222, roughness: 0.9 })
      );
      dent.scale.set(1.5, 0.8, 1);
      dent.position.set(0.9, 0.5, -1.5);
      carGroup.add(dent);

      carGroup.position.set(cx, cy, cz);
      carGroup.rotation.y = rot;
      carGroup.userData.isMapObject = true;
      carGroup.userData.hasCar = true;
      this.scene.add(carGroup);
      objects.push(carGroup);

      // Collider for car
      const box = new THREE.Box3().setFromObject(carGroup);
      colliders.push(box);
    }

    // === STREET LIGHTS ===
    for (let z = -18; z <= 18; z += 9) {
      for (const side of [-1, 1]) {
        const lx = side * 5.5;
        // Pole
        this.addMesh(
          new THREE.CylinderGeometry(0.06, 0.06, 4.5, 6),
          M(0x444444, 0.4, 0.7),
          [lx, 2.25, z], colliders, objects
        );
        // Arm
        this.addMesh(
          new THREE.BoxGeometry(1.5, 0.06, 0.06),
          M(0x444444, 0.4, 0.7),
          [lx - side * 0.7, 4.4, z], colliders, objects
        );
        // Light fixture
        const lamp = new THREE.PointLight(0xffcc88, 2, 12);
        lamp.position.set(lx - side * 1.2, 4.3, z);
        lamp.userData.isMapObject = true;
        this.scene.add(lamp);
        objects.push(lamp);
      }
    }

    // === BARRICADES & DEBRIS ===
    // Police barricade
    this.addMesh(new THREE.BoxGeometry(4, 1, 0.15), M(0xdd8800, 0.6), [-3, 0.5, -5], colliders, objects);
    this.addMesh(new THREE.BoxGeometry(4, 1, 0.15), M(0xdd8800, 0.6), [3, 0.5, 8], colliders, objects);
    // Sandbags
    for (const [sx, sz] of [[4, -10], [-4, 12]] as [number, number][]) {
      for (let i = 0; i < 3; i++) {
        this.addMesh(
          new THREE.BoxGeometry(0.8, 0.3, 0.4),
          M(0x887755),
          [sx + (i - 1) * 0.85, 0.15 + Math.floor(i / 3) * 0.3, sz],
          colliders, objects
        );
      }
    }

    // === VENDING MACHINES ===
    for (const [vx, vz, vr] of [[-9.3, -3, 0], [-9.3, 4, 0], [9.3, -10, Math.PI]] as [number, number, number][]) {
      const vm = new THREE.Mesh(
        new THREE.BoxGeometry(0.8, 1.8, 0.6),
        new THREE.MeshStandardMaterial({ color: 0x2244aa, emissive: 0x112244, emissiveIntensity: 0.3 })
      );
      vm.position.set(vx, 0.9, vz);
      vm.rotation.y = vr;
      vm.userData.isMapObject = true;
      this.scene.add(vm);
      objects.push(vm);
      colliders.push(new THREE.Box3().setFromObject(vm));
    }

    // === BLOOD STAINS on road ===
    const bloodMat = M(0x550000, 0.3);
    for (const [bx, bz] of [[-1, -3], [2, 7], [-3, 14], [4, -12], [0, 2]] as [number, number][]) {
      const pool = new THREE.Mesh(new THREE.CircleGeometry(0.3 + Math.random() * 0.5, 8), bloodMat);
      pool.rotation.x = -Math.PI / 2;
      pool.position.set(bx, 0.01, bz);
      pool.userData.isMapObject = true;
      this.scene.add(pool);
      objects.push(pool);
    }

    // === FALLEN PEOPLE (simplified for performance) ===
    const paleSkin = M(0x9a8060, 0.8);
    const deadPeople: [number, number, number, string][] = [
      [-2, -4, 0.5, 'side'],
      [3, 8, 1.8, 'face_down'],
      [-7, -10, 0, 'back'],
      [6, 3, 2.5, 'side'],
    ];

    for (const [px, pz, rotY, pose] of deadPeople) {
      const person = new THREE.Group();
      const clothMat = M([0x334455, 0x554433, 0x445544][Math.floor(Math.random() * 3)], 0.8);

      // Head
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.13, 6, 5), paleSkin);
      head.position.y = 1.5;
      person.add(head);
      // Body (single mesh)
      const body = new THREE.Mesh(new THREE.BoxGeometry(0.35, 1.0, 0.22), clothMat);
      body.position.y = 0.9;
      person.add(body);
      // Legs (single box)
      const legs = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.6, 0.2), M(0x2a2a2a));
      legs.position.y = 0.3;
      person.add(legs);

      // Apply pose
      switch (pose) {
        case 'side':
          person.rotation.z = Math.PI / 2;
          person.position.set(px, 0.13, pz);
          break;
        case 'face_down':
          person.rotation.x = Math.PI / 2;
          person.position.set(px, 0.12, pz);
          break;
        case 'back':
          person.rotation.x = -Math.PI / 2;
          person.position.set(px, 0.12, pz);
          break;
      }
      person.rotation.y = rotY;
      person.userData.isMapObject = true;
      this.scene.add(person);
      objects.push(person);

      // Blood pool under each person
      const bPool = new THREE.Mesh(new THREE.CircleGeometry(0.25 + Math.random() * 0.3, 8), bloodMat);
      bPool.rotation.x = -Math.PI / 2;
      bPool.position.set(px + (Math.random() - 0.5) * 0.2, 0.008, pz + (Math.random() - 0.5) * 0.2);
      bPool.userData.isMapObject = true;
      this.scene.add(bPool);
      objects.push(bPool);
    }

    // === SCATTERED OBJECTS on road ===
    for (let i = 0; i < 4; i++) {
      const obj = new THREE.Mesh(
        new THREE.BoxGeometry(0.15 + Math.random() * 0.2, 0.1, 0.15 + Math.random() * 0.15),
        M(0x444433 + Math.floor(Math.random() * 0x222222))
      );
      obj.position.set(
        -4 + Math.random() * 8,
        0.05,
        -20 + Math.random() * 40
      );
      obj.rotation.y = Math.random() * Math.PI;
      obj.userData.isMapObject = true;
      this.scene.add(obj);
      objects.push(obj);
    }

    // === OVERTURNED TRASH CANS ===
    for (const [tx, tz] of [[-8, -5], [8, 12], [-8, 18]] as [number, number][]) {
      const can = new THREE.Mesh(
        new THREE.CylinderGeometry(0.25, 0.3, 0.7, 8),
        M(0x555555, 0.6, 0.3)
      );
      can.position.set(tx, 0.25, tz);
      can.rotation.z = 1.2; // knocked over
      can.rotation.y = Math.random() * Math.PI;
      can.userData.isMapObject = true;
      this.scene.add(can);
      objects.push(can);
      // Trash spill
      for (let t = 0; t < 1; t++) {
        const trash = new THREE.Mesh(
          new THREE.BoxGeometry(0.1, 0.06, 0.1),
          M(0x444422 + Math.floor(Math.random() * 0x222222))
        );
        trash.position.set(tx + (Math.random() - 0.5) * 0.6, 0.03, tz + (Math.random() - 0.5) * 0.6);
        trash.rotation.y = Math.random() * Math.PI;
        trash.userData.isMapObject = true;
        this.scene.add(trash);
        objects.push(trash);
      }
    }

    return {
      colliders,
      objects,
      playerSpawn: new THREE.Vector3(0, PLAYER_HEIGHT, 0),
      zombieSpawnArea: {
        min: new THREE.Vector3(-8, 0, -20),
        max: new THREE.Vector3(8, 0, 20),
      },
    };
  }

  private buildHospital(): MapResult {
    const colliders: THREE.Box3[] = [];
    const objects: THREE.Object3D[] = [];

    const tileMat = new THREE.MeshStandardMaterial({ color: 0xcccccc, roughness: 0.6 });
    const wallMat = new THREE.MeshStandardMaterial({ color: 0xdddddd, roughness: 0.7 });
    const metalMat = new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.3, metalness: 0.7 });
    const bedMat = new THREE.MeshStandardMaterial({ color: 0xeeeeee, roughness: 0.5 });
    const deskMat = new THREE.MeshStandardMaterial({ color: 0xbbbbbb, roughness: 0.5 });
    const doorMat = new THREE.MeshStandardMaterial({ color: 0x9999aa, roughness: 0.5, metalness: 0.3 });

    // Floor (50x50)
    this.addMesh(new THREE.PlaneGeometry(50, 50), tileMat, [0, 0, 0], colliders, objects, [-Math.PI / 2, 0, 0], false);
    // Ceiling
    this.addMesh(new THREE.PlaneGeometry(50, 50), new THREE.MeshStandardMaterial({ color: 0xaaaaaa }), [0, 3.5, 0], colliders, objects, [Math.PI / 2, 0, 0], false);

    // === OUTER WALLS ===
    const W = (pos: [number, number, number], size: [number, number, number]) =>
      this.addMesh(new THREE.BoxGeometry(...size), wallMat, pos, colliders, objects);

    W([0, 1.75, -25], [50, 3.5, 0.3]);   // North
    W([0, 1.75, 25], [50, 3.5, 0.3]);    // South
    W([-25, 1.75, 0], [0.3, 3.5, 50]);   // West
    W([25, 1.75, 0], [0.3, 3.5, 50]);    // East

    // === RECEPTION AREA (South, entrance) ===
    // Reception desk (L-shaped)
    this.addMesh(new THREE.BoxGeometry(6, 1.1, 1), deskMat, [0, 0.55, 18], colliders, objects);
    this.addMesh(new THREE.BoxGeometry(1, 1.1, 3), deskMat, [-3, 0.55, 19.5], colliders, objects);
    // Chairs in waiting area
    for (let i = 0; i < 4; i++) {
      this.addMesh(new THREE.BoxGeometry(0.5, 0.45, 0.5), metalMat, [-8 + i * 2, 0.22, 22], colliders, objects);
    }
    // Reception sign
    this.addMesh(new THREE.BoxGeometry(3, 0.4, 0.05), new THREE.MeshStandardMaterial({ color: 0x2255aa }), [0, 3, 17.5], colliders, objects);

    // === MAIN CORRIDOR (North-South, center) ===
    // Left wall of main corridor
    W([-3, 1.75, 5], [0.2, 3.5, 22]);
    // Right wall of main corridor
    W([3, 1.75, 5], [0.2, 3.5, 22]);
    // Corridor opens at reception (south) and goes north

    // === EAST WING CORRIDOR ===
    W([12, 1.75, -3], [18, 3.5, 0.2]);   // South wall of east corridor
    W([12, 1.75, 3], [18, 3.5, 0.2]);    // North wall of east corridor

    // === WEST WING CORRIDOR ===
    W([-12, 1.75, -3], [18, 3.5, 0.2]);  // South wall of west corridor
    W([-12, 1.75, 3], [18, 3.5, 0.2]);   // North wall of west corridor

    // === PATIENT ROOMS (East wing) ===
    // Room dividers (4 rooms on each side of east corridor)
    for (let i = 0; i < 3; i++) {
      // South rooms dividers
      W([6 + i * 5, 1.75, -8], [0.2, 3.5, 10]);
      // North rooms dividers
      W([6 + i * 5, 1.75, 8], [0.2, 3.5, 10]);
    }
    // East wing south wall
    W([12, 1.75, -13], [18, 3.5, 0.2]);
    // East wing north wall
    W([12, 1.75, 13], [18, 3.5, 0.2]);

    // Beds in east rooms (south side)
    const eastSouthBeds = [[5, 0.4, -6], [5, 0.4, -9], [10, 0.4, -6], [10, 0.4, -9], [15, 0.4, -6], [15, 0.4, -9], [20, 0.4, -6]];
    for (const pos of eastSouthBeds) {
      this.addMesh(new THREE.BoxGeometry(1.8, 0.8, 0.8), bedMat, pos as [number, number, number], colliders, objects);
    }
    // Beds in east rooms (north side)
    const eastNorthBeds = [[5, 0.4, 6], [5, 0.4, 9], [10, 0.4, 6], [10, 0.4, 9], [15, 0.4, 6], [15, 0.4, 9], [20, 0.4, 6]];
    for (const pos of eastNorthBeds) {
      this.addMesh(new THREE.BoxGeometry(1.8, 0.8, 0.8), bedMat, pos as [number, number, number], colliders, objects);
    }

    // === PATIENT ROOMS (West wing) ===
    for (let i = 0; i < 3; i++) {
      W([-6 - i * 5, 1.75, -8], [0.2, 3.5, 10]);
      W([-6 - i * 5, 1.75, 8], [0.2, 3.5, 10]);
    }
    W([-12, 1.75, -13], [18, 3.5, 0.2]);
    W([-12, 1.75, 13], [18, 3.5, 0.2]);

    // Beds in west rooms
    const westBeds = [[-5, 0.4, -6], [-5, 0.4, -9], [-10, 0.4, -6], [-10, 0.4, -9], [-15, 0.4, -6], [-15, 0.4, -9], [-20, 0.4, -6],
                      [-5, 0.4, 6], [-5, 0.4, 9], [-10, 0.4, 6], [-10, 0.4, 9], [-15, 0.4, 6], [-15, 0.4, 9], [-20, 0.4, 6]];
    for (const pos of westBeds) {
      this.addMesh(new THREE.BoxGeometry(1.8, 0.8, 0.8), bedMat, pos as [number, number, number], colliders, objects);
    }

    // === NORTH AREA (storage/utility) ===
    W([0, 1.75, -16], [6, 3.5, 0.2]);
    // Medical cabinets
    for (const x of [-8, -5, 5, 8]) {
      this.addMesh(new THREE.BoxGeometry(0.6, 1.8, 0.4), metalMat, [x, 0.9, -20] as [number, number, number], colliders, objects);
    }

    // === MEDICAL EQUIPMENT (IV stands, monitors) ===
    const equipPositions = [[7, 0.8, -6], [12, 0.8, -6], [17, 0.8, -6], [-7, 0.8, -6], [-12, 0.8, -6],
                            [7, 0.8, 6], [12, 0.8, 6], [-7, 0.8, 6], [-12, 0.8, 6]];
    for (const pos of equipPositions) {
      this.addMesh(new THREE.BoxGeometry(0.3, 1.6, 0.3), metalMat, pos as [number, number, number], colliders, objects);
    }

    // === BLUE CURTAINS at room entrances (along corridors) ===
    const curtainMat = new THREE.MeshStandardMaterial({
      color: 0x4466aa, roughness: 0.9, transparent: true, opacity: 0.7, side: THREE.DoubleSide,
    });
    // East wing curtains (corridor side, z=-3 and z=3)
    for (let i = 0; i < 4; i++) {
      const cx = 4 + i * 5;
      // South room curtains
      this.addMesh(new THREE.PlaneGeometry(3.5, 2.8), curtainMat, [cx, 1.4, -3.1] as [number, number, number], colliders, objects, [0, 0, 0], false);
      // North room curtains
      this.addMesh(new THREE.PlaneGeometry(3.5, 2.8), curtainMat, [cx, 1.4, 3.1] as [number, number, number], colliders, objects, [0, 0, 0], false);
    }
    // West wing curtains
    for (let i = 0; i < 4; i++) {
      const cx = -4 - i * 5;
      this.addMesh(new THREE.PlaneGeometry(3.5, 2.8), curtainMat, [cx, 1.4, -3.1] as [number, number, number], colliders, objects, [0, 0, 0], false);
      this.addMesh(new THREE.PlaneGeometry(3.5, 2.8), curtainMat, [cx, 1.4, 3.1] as [number, number, number], colliders, objects, [0, 0, 0], false);
    }
    // Curtain rails (metal rods above curtains)
    const railMat = new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.3, metalness: 0.7 });
    for (const cx of [4, 9, 14, 19, -4, -9, -14, -19]) {
      for (const cz of [-3.1, 3.1]) {
        this.addMesh(new THREE.CylinderGeometry(0.015, 0.015, 4, 4), railMat, [cx, 2.9, cz] as [number, number, number], colliders, objects, [0, 0, Math.PI / 2], false);
      }
    }

    // === PATIENTS ON BEDS (zombies lying down, appear dead/sleeping) ===
    const patientSkinMat = new THREE.MeshStandardMaterial({ color: 0x7a8a6a, roughness: 0.8 });
    const gownMat = new THREE.MeshStandardMaterial({ color: 0x88bbbb, roughness: 0.8 });
    const blanketMat = new THREE.MeshStandardMaterial({ color: 0xddddee, roughness: 0.7 });
    // Place patients on some beds (not all, to be creepy)
    const patientBeds = [
      [5, -6], [10, -9], [15, -6], [-5, -9], [-10, -6], [-15, -9],
      [5, 9], [10, 6], [-5, 6], [-15, 9], [20, -6], [-20, -6],
    ];
    for (const [px, pz] of patientBeds) {
      const pg = new THREE.Group();
      // Body (under blanket)
      const body = new THREE.Mesh(new THREE.BoxGeometry(1.4, 0.2, 0.5), blanketMat);
      body.position.y = 0.9;
      pg.add(body);
      // Head (sticking out)
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.1, 6, 5), patientSkinMat);
      head.position.set(-0.6, 0.95, 0);
      pg.add(head);
      // Arms (one hanging off bed)
      const arm = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.3, 0.06), patientSkinMat);
      arm.position.set(0, 0.65, 0.3);
      pg.add(arm);
      // Hospital gown visible
      const gown = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.15, 0.45), gownMat);
      gown.position.set(-0.3, 0.92, 0);
      pg.add(gown);

      pg.position.set(px, 0, pz);
      pg.rotation.y = Math.random() * 0.3 - 0.15;
      pg.userData.isMapObject = true;
      this.scene.add(pg);
      objects.push(pg);
    }

    // === LIGHTING ===
    const lightPositions = [
      [0, 3, 20], [0, 3, 10], [0, 3, 0], [0, 3, -10], [0, 3, -20],
      [10, 3, 0], [-10, 3, 0], [10, 3, -8], [-10, 3, -8], [10, 3, 8], [-10, 3, 8],
      [20, 3, 0], [-20, 3, 0],
    ];
    for (const pos of lightPositions) {
      const light = new THREE.PointLight(0xeeeeff, 0.4, 12);
      light.position.set(pos[0], pos[1], pos[2]);
      light.userData.isMapObject = true;
      this.scene.add(light);
      objects.push(light);
    }
    // Emergency green lights
    for (const pos of [[0, 3, -22], [22, 3, 0], [-22, 3, 0]]) {
      const eLight = new THREE.PointLight(0x44ff44, 0.15, 6);
      eLight.position.set(pos[0], pos[1], pos[2]);
      eLight.userData.isMapObject = true;
      this.scene.add(eLight);
      objects.push(eLight);
    }

    return {
      colliders,
      objects,
      playerSpawn: new THREE.Vector3(0, PLAYER_HEIGHT, 20),
      zombieSpawnArea: {
        min: new THREE.Vector3(-22, 0, -22),
        max: new THREE.Vector3(22, 0, 15),
      },
    };
  }

  private buildGenericIndoor(stageId: number): MapResult {
    const colliders: THREE.Box3[] = [];
    const objects: THREE.Object3D[] = [];

    // Color themes per stage
    const themes: Record<number, { floor: number; wall: number; accent: number; light: number }> = {
      4: { floor: 0x2a3a2a, wall: 0x3a4a3a, accent: 0x445544, light: 0x44aa44 },  // Sewer
      5: { floor: 0x3a3a2a, wall: 0x4a4a3a, accent: 0x556644, light: 0xaaaa44 },  // Military
      6: { floor: 0x2a2a3a, wall: 0x3a3a4a, accent: 0x445566, light: 0x4444aa },  // Subway
      7: { floor: 0x3a3a3a, wall: 0x4a4a4a, accent: 0x666666, light: 0xff6600 },  // Factory
      8: { floor: 0x2a3a1a, wall: 0x3a4a2a, accent: 0x556633, light: 0x668844 },  // Forest
      9: { floor: 0x3a3a3a, wall: 0x555555, accent: 0x4488aa, light: 0x88aacc },  // Airport
      10: { floor: 0x1a1a2a, wall: 0x2a2a3a, accent: 0x4444aa, light: 0xff0044 }, // Final Lab
    };

    const theme = themes[stageId] ?? themes[4];
    const floorMat = new THREE.MeshStandardMaterial({ color: theme.floor, roughness: 0.9 });
    const wallMat = new THREE.MeshStandardMaterial({ color: theme.wall, roughness: 0.8 });
    const accentMat = new THREE.MeshStandardMaterial({ color: theme.accent, roughness: 0.6 });

    const mapSize = 30 + (stageId - 1) * 2; // Maps get slightly bigger
    const halfSize = mapSize / 2;

    // Floor
    this.addMesh(
      new THREE.PlaneGeometry(mapSize, mapSize),
      floorMat,
      [0, 0, 0],
      colliders, objects,
      [-Math.PI / 2, 0, 0],
      false
    );

    // Ceiling (indoor stages)
    if (stageId !== 8) {
      this.addMesh(
        new THREE.PlaneGeometry(mapSize, mapSize),
        new THREE.MeshStandardMaterial({ color: theme.wall }),
        [0, 4, 0],
        colliders, objects,
        [Math.PI / 2, 0, 0],
        false
      );
    }

    // Walls
    const wallH = stageId === 8 ? 0.5 : 4;
    const wallDefs = [
      { pos: [0, wallH / 2, -halfSize] as [number, number, number], size: [mapSize, wallH, 0.5] as [number, number, number] },
      { pos: [0, wallH / 2, halfSize] as [number, number, number], size: [mapSize, wallH, 0.5] as [number, number, number] },
      { pos: [-halfSize, wallH / 2, 0] as [number, number, number], size: [0.5, wallH, mapSize] as [number, number, number] },
      { pos: [halfSize, wallH / 2, 0] as [number, number, number], size: [0.5, wallH, mapSize] as [number, number, number] },
    ];
    for (const w of wallDefs) {
      this.addMesh(new THREE.BoxGeometry(...w.size), wallMat, w.pos, colliders, objects);
    }

    // Random obstacles
    const obstacleCount = 8 + stageId;
    for (let i = 0; i < obstacleCount; i++) {
      const x = (Math.random() - 0.5) * (mapSize - 4);
      const z = (Math.random() - 0.5) * (mapSize - 4);

      // Don't place too close to player spawn
      if (Math.abs(x) < 3 && Math.abs(z) < 3) continue;

      const type = Math.random();
      if (type < 0.4) {
        // Box obstacle
        const w = 1 + Math.random() * 2;
        const h = 0.5 + Math.random() * 1.5;
        this.addMesh(
          new THREE.BoxGeometry(w, h, w * 0.6),
          accentMat,
          [x, h / 2, z],
          colliders, objects
        );
      } else if (type < 0.7) {
        // Cylinder
        const r = 0.3 + Math.random() * 0.4;
        this.addMesh(
          new THREE.CylinderGeometry(r, r, 1 + Math.random(), 8),
          accentMat,
          [x, 0.5, z],
          colliders, objects
        );
      } else {
        // Wall segment
        const len = 2 + Math.random() * 4;
        const angle = Math.random() * Math.PI;
        const wall = this.addMesh(
          new THREE.BoxGeometry(len, 3, 0.2),
          wallMat,
          [x, 1.5, z],
          colliders, objects,
          [0, angle, 0]
        );
      }
    }

    // Lights
    const lightCount = 4 + Math.floor(stageId / 2);
    for (let i = 0; i < lightCount; i++) {
      const lx = (Math.random() - 0.5) * (mapSize - 4);
      const lz = (Math.random() - 0.5) * (mapSize - 4);
      const light = new THREE.PointLight(theme.light, 0.2, 8);
      light.position.set(lx, 3, lz);
      light.userData.isMapObject = true;
      this.scene.add(light);
      objects.push(light);
    }

    return {
      colliders,
      objects,
      playerSpawn: new THREE.Vector3(0, PLAYER_HEIGHT, 0),
      zombieSpawnArea: {
        min: new THREE.Vector3(-halfSize + 2, 0, -halfSize + 2),
        max: new THREE.Vector3(halfSize - 2, 0, halfSize - 2),
      },
    };
  }
}

const PLAYER_HEIGHT = 1.7;
